import { useEffect, useRef, useState } from "react";
import {
  ArrowBigLeftDash,
  Bird,
  FileBadge,
  FileBox,
  FileClock,
  LayoutDashboard
} from "lucide-react";
import SlideMenuGenerator from "./slidemenugenerator";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { ContextMenuItem } from "@radix-ui/react-context-menu";
import { useUser } from "@/components/hooks/useUser";
import { User } from "@/components/api/modal";

export function SideMenu() {
  const [isOpen, setOpen] = useState(
    localStorage.getItem("sideMenuOpen") === "true" ? true : false
  );

  const toggleOpen = () => {
    localStorage.setItem("sideMenuOpen", !isOpen ? "true" : "false");
    setOpen(!isOpen);
  };

  const [showFooterItems, setShowFooterItems] = useState(false);

  const {data:user, isFetched} = useUser()

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowFooterItems(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleButtonClick = () => {
    setShowFooterItems((prevState) => !prevState);
  };

  const isAdmin = (user: any): JSX.Element | null => {
    if (user && user.rolls && user.rolls.includes("Administrators")) {
      return (
        <div
          className={`${isOpen ? "space-y-6" : "space-y-8"
            } py-2.5 transition-all duration-500 border-t border-t-harvey-blue`}
        >
          <ContextMenu>
            <ContextMenuTrigger className="flex h-[80px] items-center justify-center rounded border border-gray-600 text-sm">
              Admin Menu
            </ContextMenuTrigger>
            <ContextMenuContent className="w-64 p-4 border-gray-600 space-y-3">
              <ContextMenuItem>
                <a className="text-white" href="/_dev/design-elements" target="_blank" rel="noopener noreferrer">
                  Design Elements
                </a>
              </ContextMenuItem>
              <ContextMenuItem>
                <a className="text-white" href="/_dev/example" target="_blank" rel="noopener noreferrer">
                  Example
                </a>
              </ContextMenuItem>
              <ContextMenuItem>
                <a className="text-white" href="/_services/about" target="_blank" rel="noopener noreferrer">
                  Cache Reset
                </a>
              </ContextMenuItem>
              <ContextMenuItem>
                <a
                  className="text-white"
                  href="https://portaladmin-oce.portal-infra.dynamics.com/?tenantProductId=14bd58ac-7a5c-4bca-83ea-c2185af6df04&lcid=1033&geo=OCE"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Power Portal Admin
                </a>
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="dark z-10 bg-harvey-black w-auto flex min-h-screen">
      <div
        className={`${isOpen ? "lg:w-60 md:w-60 sm:w-48 p-5" : "w-14 p-1 items-center"
          } relative duration-300 flex flex-col shadow-xl shadow-gray-600`}
      >
        {/*------------------------------------ Side Menu Button  --------------------------------------------*/}
        <div
          className={`z-[1] transition-all duration-500 bg-gray-600 absolute cursor-pointer h-8 px-0 -right-2 top-9 w-8 text-white items-center justify-center flex rounded shadow-[0_35px_60px_-15px_rgba(0,0,0,0.3)]
          ${!isOpen && " rotate-[180deg]"}`}
          onClick={toggleOpen}
        >
          <ArrowBigLeftDash />
        </div>
        {/* ----------------------------------------------Greeting ------------------------------------------ */}
        {isOpen ? (
          <h2 className="mb-4 p-3 text-lg font-semibold tracking-tight capitalize">
            {user?.result
              ? `Kia ora ${user?.result.firstname}`
              : "Kia ora"}
          </h2>
        ) : (
          <></>
        )}

        <div
          className={`${isOpen ? "space-y-6" : "space-y-8 mt-24"
            } py-2.5 transition-all duration-500 border-b border-b-harvey-blue`}
        >
          {/* --------------------------DashBoard Generator Block ----------------------------*/}
          <SlideMenuGenerator
            navigateTo="/customer-portal/"
            menuName="Dashboard"
            Icon={{ menuIcon: LayoutDashboard, iconSize: 20 }}
            isOpen={isOpen}
          />
        </div>

        {/* Sales Items  */}
        <div
          className={`${isOpen ? "space-y-6" : "space-y-8"
            } py-2.5 transition-all duration-500 border-b border-b-harvey-blue`}
        >
          {/* --------------------------Price List Generator Block ----------------------------*/}
          <SlideMenuGenerator
            navigateTo="/customer-portal/pricelist"
            menuName="Price List"
            Icon={{ menuIcon: FileBadge, iconSize: 20 }}
            isOpen={isOpen}
          />

          {/* --------------------------Quotation Generator Block ----------------------------*/}
          <SlideMenuGenerator
            navigateTo="/customer-portal/quotations"
            menuName="Quotations"
            Icon={{ menuIcon: FileClock, iconSize: 20 }}
            isOpen={isOpen}
            hooverCardName="quotations"
            userFirstName={user?.result.firstname}
          />

          {/* --------------------------Orders Generator Block ----------------------------*/}
          <SlideMenuGenerator
            navigateTo="/customer-portal/orders"
            menuName="Orders"
            Icon={{ menuIcon: FileBox, iconSize: 20 }}
            isOpen={isOpen}
          />

          {/* --------------------------Invoices Generator Block --------------------------*/}
          <SlideMenuGenerator
            navigateTo="/customer-portal/invoices"
            menuName="Invoices"
            Icon={{ menuIcon: FileBadge, iconSize: 20 }}
            isOpen={isOpen}
          />

          {/* --------------------------Pay Now Generator Block ----------------------------*/}

          {/* <SlideMenuGenerator 
                            navigateTo="/pay-now"
                            openDialog={true}
                            menuName="Pay Now"

                            Icon={{menuIcon:CreditCard , iconSize:20}}
                            isOpen={isOpen}
                            /> */}

        </div>
        {/* ---------------------------------Service and Support--------------------------- */}

        <div className=" space-y-1.5 py-2.5 border-b-1 border-b-harvey-blue">
          <h2
            className={`${isOpen && "relative px-6 py-1.5 font-semibold tracking-tight"
              }`}
          >
            <span className={`${!isOpen && "hidden"}`}>
              Service and Support
            </span>
          </h2>

          {/* --------------------------Knowledge Base Generator Block ----------------------------*/}

          <SlideMenuGenerator
            navigateTo="/paynow"
            menuName="Knowledge Base"
            Icon={{ menuIcon: Bird, iconSize: 20 }}
            isOpen={isOpen}
          />
        </div>

        {isFetched ? isAdmin(user?.result as User) : <></>}

        {/*--------------------------------------------- Footer  ----------------------------------*/}
        {/*
          <div
            className={`${isOpen ? "flex md:w-48 sm:w-26" : "w-14"
              } flex-row justify-around items-center`}
          >
     
            {isOpen && <Footer_Row />}
            {!isOpen && (
              <span
                className="text-xl ml-4 flex group absolute bottom-0 cursor-pointer"
                ref={buttonRef}
              >
                <div onClick={handleButtonClick}>
                  <BiDotsVerticalRounded className="text-white" />
                </div>
              </span>
            )}
            {!isOpen && showFooterItems && (
              <div
                className="flex flex-row bg-gray-800 absolute bottom-14 left-4 pl-2 md:animate-pulse"
                onClick={handleFooterClick}
              >
                <Footer_List />
              </div>
            )}
          </div>
       */}
      </div>
    </div>
  );
}
